import * as React from 'react';
import NewTableContext, { FrozenCellWidthCache } from '../NewTableContext';

/*
  `offset` is the sum of the width of all frozen cells before the current cell.
*/
export const computeCache = (tableRef: HTMLTableElement | null): FrozenCellWidthCache => {
  if (!tableRef) {
    return {};
  }

  const firstFrozenCell = tableRef.querySelector('tr [data-freeze]');
  const firstRowWithFrozenCells = firstFrozenCell?.parentElement;

  if (!firstRowWithFrozenCells) {
    /*
      If this table doesn't have any frozen cells, we don't need to do anything.
    */
    return {};
  }

  const frozenCells = firstRowWithFrozenCells.querySelectorAll('[data-freeze]');

  if (frozenCells.length === 1) {
    /*
      If there's only one frozen cell, we already know what we need to know
    */
    return { '0': [0, true] };
  }

  const { cache } = Array.from(frozenCells).reduce(
    (acc, cell, index) => {
      const { offset, cache } = acc;

      const nextOffset = offset + cell.clientWidth;
      const isLastFrozenCell = index === frozenCells.length - 1;

      acc.cache[index] = [offset, isLastFrozenCell];

      return { offset: nextOffset, cache };
    },
    {
      offset: 0,
      cache: {} as Record<string, [number, boolean]>,
    },
  );

  return cache;
};

/**
 * Uses the first row with frozen cells to calculate the offset of each frozen cell.
 * This is relatively expensive, so we use this to only do it once, maybe twice.
 *
 * This hook is used in <NewTable> and its value is passed to <NewTableContext.Provider>
 */
export const useCalculateFrozenCellSettings = (tableRef: React.RefObject<HTMLTableElement>): FrozenCellWidthCache => {
  const [cache, setCache] = React.useState<Record<number, [number, boolean]>>({});

  /** We keep track of the last width so that the resize observer doesn't fire hundreds
   * of times when scrolling a virtualized table.
   */
  const lastWidth = React.useRef(0);

  React.useLayoutEffect(() => {
    if (!tableRef.current) {
      /*
        If we don't have a table element, we can't do anything - table hasn't finished mounting yet.
      */
      return;
    }

    setCache(computeCache(tableRef.current));

    /*
      The `resizeObserver` ensures that the offset is correctly computed everytime
      NewTable's width changes. This doesn't happen super often but typcially occurs on
      initial render resulting in the initially calculated (cached) offset
      no longer being correct, causing cells to "fold" over each other.
    */

    const resizeObserver = new ResizeObserver(entries => {
      if (!entries?.length) {
        return;
      }
      const { contentBoxSize } = entries[0];

      const width = contentBoxSize[0].inlineSize;
      if (width === lastWidth.current) {
        return;
      }
      lastWidth.current = width;

      setCache(() => computeCache(tableRef.current));
    });

    const mutationObserver = new MutationObserver(() => {
      return setCache(() => computeCache(tableRef.current));
    });

    resizeObserver.observe(tableRef.current);
    mutationObserver.observe(tableRef.current, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-freeze', 'class'],
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [tableRef]);

  return cache;
};

/*
  Uses a callback ref to get the cell's position in the row,
  and then use that to get the offset from the cache.

  Used in <NewTableCell> and <NewTableHeadCell>
*/
export const useCachedFrozenCellSettings = (): {
  cellRef: React.RefCallback<HTMLTableCellElement>;
  offsetLeft: number;
  lastFrozenColumn: boolean;
} => {
  const [[offsetLeft, lastFrozenColumn], setCellCache] = React.useState<[number, boolean]>([0, false]);

  const { frozenCellWidthCache: cache } = React.useContext(NewTableContext);

  const callbackRef = React.useCallback(
    (node: HTMLTableCellElement) => {
      if (!node) {
        return;
      }

      const parentRow = node.parentElement;
      const rowCellIndex = Array.from(parentRow!.children).indexOf(node);

      const cellCache = cache[rowCellIndex];

      if (!cellCache) {
        setCellCache([0, false]);
        return;
      }

      if (cellCache[0] !== offsetLeft || cellCache[1] !== lastFrozenColumn) {
        setCellCache(cellCache);
      }
    },
    [cache, offsetLeft, lastFrozenColumn],
  );

  return {
    cellRef: callbackRef,
    offsetLeft,
    lastFrozenColumn,
  };
};
